import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import axios from 'axios';
import { FC, useEffect, useMemo, useState } from 'react';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { APPROVAL_STATUS_OPTIONS } from './constants';
import { TVerificationCorrectionActionDialogProps } from './types';
import { formatCommencementDate, getSchemeLabel, getVerificationCorrectionBanner } from './utils';
import {
	EM_VAT_CORRECTIONS_IMPORT_VIEW_TABLE,
	EM_VAT_CORRECTIONS_NON_EU_VIEW_TABLE,
	EM_VAT_CORRECTIONS_NON_UNION_VIEW_TABLE,
	EM_VAT_CORRECTIONS_UNION_VIEW_TABLE,
} from '../../../redux/reducers/emVatCorrectionsImport/constants';
import { COLORS } from '../../../services/constants/colors';

export const VerificationCorrectionActionDialog: FC<TVerificationCorrectionActionDialogProps> = ({
	open,
	row,
	tableName,
	onClose,
	onSuccess,
}) => {
	// const tableReportName = useMemo(() => {
	// 	return tableName === EM_VAT_CORRECTIONS_IMPORT_VIEW_TABLE
	// 		? 'em_vat_report_vat_corrections_import'
	// 		: tableName === EM_VAT_CORRECTIONS_NON_UNION_VIEW_TABLE
	// 			? 'em_vat_report_vat_corrections_non_union'
	// 			: tableName === EM_VAT_CORRECTIONS_UNION_VIEW_TABLE
	// 				? 'em_vat_report_vat_corrections_union'
	// 				: tableName === EM_VAT_CORRECTIONS_NON_EU_VIEW_TABLE
	// 					? 'em_vat_report_vat_corrections_non_eu_import'
	// 					: 'em_vat_report_vat_corrections';
	// }, [tableName]);
	const [approvalStatus, setApprovalStatus] = useState('');
	const [remarks, setRemarks] = useState(row?.action_remarks ?? '');
	const [submitting, setSubmitting] = useState(false);

	const banner = useMemo(() => (row ? getVerificationCorrectionBanner(row) : null), [row]);

	useEffect(() => {
		if (open) {
			setApprovalStatus('');
			setRemarks(row?.action_remarks ?? '');
		}
	}, [open, row?.action_remarks]);

	const schemeLabel = row ? getSchemeLabel(row.scheme) : '';

	const handleClose = () => {
		if (!submitting) {
			onClose();
		}
	};

	const handleSubmit = async () => {
		if (!row || !approvalStatus) {
			notificationContainer('Please select an action.', 'error');
			return;
		}

		const bannerVisible = banner?.visible ?? false;
		const bannerMsg = bannerVisible ? (banner?.message ?? '') : '';

		setSubmitting(true);
		try {
			await api.emVatCorrectionsImport.submitCorrectionAction({
				id: row.id,
				bannerVisible: bannerVisible,
				bannerMsg: bannerMsg,
				approvalStatus: approvalStatus,
				actionRemarks: remarks.trim(),
				tableName: tableName,
			});
			notificationContainer('Correction action saved.', 'success');
			onSuccess();
			onClose();
		} catch (error) {
			if (axios.isAxiosError(error)) {
				notificationContainer(
					String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
					'error',
				);
			} else {
				notificationContainer('Request failed.', 'error');
			}
		} finally {
			setSubmitting(false);
		}
	};
	const handleCommentSubmit = async () => {
		if (!row || !remarks) {
			notificationContainer('Please select an action.', 'error');
			return;
		}

		setSubmitting(true);
		try {
			await api.emVatCorrectionsImport.submitCorrectionAction({
				id: row.id,
				approvalStatus: 'saveComment',
				actionRemarks: remarks.trim(),
				tableName: tableName,
			});
			notificationContainer('Correction action saved.', 'success');
			onSuccess();
			onClose();
		} catch (error) {
			if (axios.isAxiosError(error)) {
				notificationContainer(
					String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
					'error',
				);
			} else {
				notificationContainer('Request failed.', 'error');
			}
		} finally {
			setSubmitting(false);
		}
	};

	if (!row) {
		return null;
	}

	return (
		<Dialog
			open={open}
			onClose={submitting ? () => {} : handleClose}
			fullWidth
			maxWidth="sm"
			PaperProps={{
				sx: { borderRadius: '12px' },
			}}
		>
			<DialogTitle color="primary">
				Verification correction {schemeLabel} #{row.id}
			</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
					{banner?.visible && (
						<Alert severity="error" variant="filled" sx={{ borderWidth: 2 }}>
							{banner.message}
						</Alert>
					)}

					<TableContainer
						sx={{
							'& .MuiTableCell-root': { border: 'none', verticalAlign: 'middle' },
						}}
					>
						<Table size="small" sx={{ tableLayout: 'fixed' }}>
							<TableBody>
								<TableRow>
									<TableCell sx={{ width: '38%', py: 0.5, px: 0, pr: 1 }}>
										<Typography variant="body2">EM name:</Typography>
									</TableCell>
									<TableCell sx={{ py: 0.5, px: 0 }}>
										<Typography variant="body2" fontWeight="bold" color={COLORS.PRIMARY}>
											{row.full_name || '—'}
										</Typography>
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell sx={{ py: 0.5, px: 0, pr: 1 }}>
										<Typography variant="body2">{`${getSchemeLabel(row.scheme)} ID:`}</Typography>
									</TableCell>
									<TableCell sx={{ py: 0.5, px: 0 }}>
										<Typography variant="body2" fontWeight="bold" color={COLORS.PRIMARY}>
											{row.scheme_num ?? '—'}
										</Typography>
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell sx={{ py: 0.5, px: 0, pr: 1 }}>
										<Typography variant="body2">Commencement date:</Typography>
									</TableCell>
									<TableCell sx={{ py: 0.5, px: 0 }}>
										<Typography variant="body2" fontWeight="bold" color={COLORS.PRIMARY}>
											{formatCommencementDate(row.scheme_date)}
										</Typography>
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell sx={{ py: 0.5, px: 0, pr: 1 }}>
										<Typography variant="body2">Correction period:</Typography>
									</TableCell>
									<TableCell sx={{ py: 0.5, px: 0 }}>
										<Typography variant="body2" fontWeight="bold" color={COLORS.PRIMARY}>
											{row.correction_peeriod || '—'}
										</Typography>
									</TableCell>
								</TableRow>
								{/* <TableRow>
									<TableCell sx={{ py: 0.5, px: 0, pr: 1 }}>
										<Typography variant="body2">Value:</Typography>
									</TableCell>
									<TableCell sx={{ py: 0.5, px: 0 }}>
										<AmountAndCurrency
											amount={row.requested_value_correction}
											currency={row.order_currency ?? 'EUR'}
											color={COLORS.PRIMARY}
											bold="bold"
										/>
									</TableCell>
								</TableRow> */}
								<TableRow>
									<TableCell sx={{ py: 0.5, px: 0, pr: 1 }}>
										<Typography variant="body2">Country:</Typography>
									</TableCell>
									<TableCell sx={{ py: 0.5, px: 0 }}>
										<CountryWithFlag countryCode={row.country} color="primary" bold />
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>

					<FormControl size="small" fullWidth required>
						<InputLabel id="correction-action-select-label">Action</InputLabel>
						<Select
							labelId="correction-action-select-label"
							label="Action"
							value={approvalStatus}
							onChange={(e) => setApprovalStatus(e.target.value)}
							disabled={submitting}
						>
							<MenuItem value="">
								<em>Select action</em>
							</MenuItem>
							{APPROVAL_STATUS_OPTIONS.map((o) => (
								<MenuItem key={o.value} value={o.value}>
									{o.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						label="Remarks"
						size="small"
						fullWidth
						multiline
						minRows={4}
						value={remarks}
						onChange={(e) => setRemarks(e.target.value)}
						disabled={submitting}
					/>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose} disabled={submitting}>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={() => {
						void handleCommentSubmit();
					}}
					disabled={submitting || !remarks}
				>
					{submitting ? <CircularProgress size={22} color="inherit" /> : 'Save Comment only'}
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={() => {
						void handleSubmit();
					}}
					disabled={submitting || !approvalStatus}
				>
					{submitting ? <CircularProgress size={22} color="inherit" /> : 'OK'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
